// ===========================================
// Roadmap Kanban - Drag & Drop Feature Request Board
// ===========================================
// Kanban board for managing project feature requests
// Supports drag & drop between columns and priority management

import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import {
  DndContext,
  DragOverlay,
  pointerWithin,
  rectIntersection,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy
} from '@dnd-kit/sortable';
import { useDroppable } from '@dnd-kit/core';
import { useProjects } from '../context/ProjectContext';
import { useAuth } from '../context/AuthContext';
import RoadmapCard from './RoadmapCard';
import { Plus, ChevronDown, ChevronRight } from 'lucide-react';
import './RoadmapKanban.scss';

// ===========================================
// Column Definitions - Roadmap Stages
// ===========================================
// Maps to the milestone status values in the backend schema
const ROADMAP_COLUMNS = [
  { 
    id: 'pending', 
    label: 'Backlog', 
    color: '#94a3b8',
    description: 'Planned items'
  },
  { 
    id: 'in-progress', 
    label: 'In Progress', 
    color: '#00d4ff',
    description: 'Currently working'
  },
  { 
    id: 'completed', 
    label: 'Done', 
    color: '#10b981',
    description: 'Shipped'
  },
  // Archived column for retired/deprioritized requests
  { 
    id: 'archived', 
    label: 'Archived', 
    color: '#6b7280',
    description: 'Retired requests'
  }
];

// ===========================================
// LocalStorage key for fallback persistence
// ===========================================
// Primary storage is user profile; localStorage is fallback for offline/migration
const ROADMAP_COLLAPSED_KEY = 'devpad_roadmap_collapsed';

// ===========================================
// Droppable Column Component
// ===========================================
// Each column is a droppable zone that accepts milestone cards
// Supports collapsing to save horizontal space
const RoadmapColumn = ({ column, items, onAddClick, projectId, onEditItem, isCollapsed, onToggleCollapse }) => {
  // Setup droppable zone for cards in this column
  const { setNodeRef, isOver } = useDroppable({
    id: column.id,
    data: { column }
  });

  // Get item IDs for sortable context (handle both _id and id for MongoDB compatibility)
  const itemIds = items.map(item => item._id || item.id);

  // Build class names based on state
  const columnClasses = [
    'roadmap-column',
    isOver ? 'roadmap-column--over' : '',
    isCollapsed ? 'roadmap-column--collapsed' : ''
  ].filter(Boolean).join(' ');

  return (
    // Outer div is the droppable zone - allows dropping even when collapsed
    <div ref={setNodeRef} className={columnClasses}>
      {/* Column Header */}
      <div className="roadmap-column__header">
        <div 
          className="roadmap-column__status-dot"
          style={{ background: column.color }}
        />
        {/* Column title - click to collapse/expand */}
        <h3 
          className="roadmap-column__title"
          onClick={() => onToggleCollapse?.(column.id)}
        >
          {column.label}
        </h3>
        <span className="roadmap-column__count">{items.length}</span>
        
        {/* Add button - only show when expanded */}
        {!isCollapsed && (
          <button 
            className="roadmap-column__add"
            onClick={() => onAddClick(column.id)}
            title={`Add request to ${column.label}`}
          >
            <Plus size={14} />
          </button>
        )}
        
        {/* Collapse toggle button */}
        <button 
          className="roadmap-column__toggle"
          onClick={() => onToggleCollapse?.(column.id)}
          title={isCollapsed ? 'Expand column' : 'Collapse column'}
        >
          {isCollapsed ? <ChevronRight size={14} /> : <ChevronDown size={14} />}
        </button>
      </div>

      {/* Column Content - cards area (hidden when collapsed) */}
      {!isCollapsed && (
        <div className="roadmap-column__content">
          <SortableContext items={itemIds} strategy={verticalListSortingStrategy}>
            {items.length > 0 ? (
              items.map(item => (
                <RoadmapCard 
                  key={item._id || item.id} 
                  item={item}
                  projectId={projectId}
                  onEdit={onEditItem}
                />
              ))
            ) : (
              <div className="roadmap-column__empty">
                <span>No requests</span>
                <p>Drag requests here</p>
              </div>
            )}
          </SortableContext>
        </div>
      )}

      {/* Collapsed indicator - shows item count when minimized */}
      {isCollapsed && items.length > 0 && (
        <div className="roadmap-column__collapsed-indicator">
          <span>{items.length} request{items.length !== 1 ? 's' : ''}</span>
        </div>
      )}
    </div>
  );
};

// ===========================================
// Main Roadmap Kanban Component
// ===========================================
const RoadmapKanban = ({ roadmap = [], projectId, onAddMilestone, onEditMilestone }) => {
  const { updateMilestone } = useProjects();
  const { user, updateProfile } = useAuth();
  
  // Track currently dragging item for overlay
  const [activeItem, setActiveItem] = useState(null);

  // Ref to track if this is the initial mount (skip saving on first render)
  const isInitialMount = useRef(true);
  // Ref to store user preferences for stable callback reference
  const userPrefsRef = useRef(user?.preferences);

  // Keep the ref updated with latest user preferences
  useEffect(() => {
    userPrefsRef.current = user?.preferences;
  }, [user?.preferences]);

  // ===========================================
  // Collapsed columns state (persisted to user profile)
  // ===========================================
  // Tracks which columns the user has minimized to save horizontal space
  // Priority: user preferences > localStorage fallback > default (empty)
  const [collapsedColumns, setCollapsedColumns] = useState(() => {
    // Check user profile preferences first
    const userCollapsed = user?.preferences?.roadmap?.collapsedColumns;
    if (userCollapsed && userCollapsed.length > 0) {
      return new Set(userCollapsed);
    }
    
    // Fallback to localStorage for migration/offline
    const localCollapsed = localStorage.getItem(ROADMAP_COLLAPSED_KEY);
    if (localCollapsed) {
      try {
        return new Set(JSON.parse(localCollapsed));
      } catch {
        return new Set();
      }
    }
    return new Set();
  });

  // ===========================================
  // Debounced save to user profile
  // ===========================================
  // Saves preferences after 500ms of no changes to avoid excessive API calls
  const saveTimeoutRef = useRef(null);

  // Stable callback that uses refs to avoid dependency changes causing re-renders
  const savePreferences = useCallback((newCollapsedColumns) => {
    // Clear any pending save
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    
    // Debounce the save
    saveTimeoutRef.current = setTimeout(async () => {
      const collapsedIds = Array.from(newCollapsedColumns);
      
      // Save to localStorage as immediate backup
      localStorage.setItem(ROADMAP_COLLAPSED_KEY, JSON.stringify(collapsedIds));
      
      // Save to user profile (syncs across devices)
      // Use ref to get current preferences to avoid callback dependency issues
      try {
        await updateProfile({
          preferences: {
            ...userPrefsRef.current,
            roadmap: {
              ...userPrefsRef.current?.roadmap,
              collapsedColumns: collapsedIds
            }
          }
        });
      } catch (err) {
        // Silently fail - localStorage backup is already saved
        console.error('Failed to save roadmap preferences:', err);
      }
    }, 500);
  }, [updateProfile]);

  // Save when collapsed state changes (but not on initial mount)
  useEffect(() => {
    // Skip the initial render to avoid unnecessary API call on page load
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    savePreferences(collapsedColumns);
  }, [collapsedColumns, savePreferences]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, []);

  // Toggle column collapse handler
  const handleToggleCollapse = (columnId) => {
    setCollapsedColumns(prev => {
      const newSet = new Set(prev);
      if (newSet.has(columnId)) {
        newSet.delete(columnId); // Expand the column
      } else {
        newSet.add(columnId); // Collapse the column
      }
      return newSet;
    });
  };

  // ===========================================
  // Setup drag sensors
  // ===========================================
  // PointerSensor for mouse/touch, KeyboardSensor for accessibility
  const sensors = useSensors(
    useSensor(PointerSensor, {
      // Require some movement before starting drag (prevents accidental drags)
      activationConstraint: {
        distance: 5
      }
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates
    })
  );

  // ===========================================
  // Custom collision detection strategy
  // ===========================================
  // Uses pointerWithin for better detection when dragging to empty columns
  // Falls back to rectIntersection for card-to-card detection
  const collisionDetectionStrategy = (args) => {
    // First try rectIntersection to detect card-to-card collisions
    const rectCollisions = rectIntersection(args);
    
    // Filter to find card collisions (IDs that are NOT column IDs)
    const cardCollisions = rectCollisions.filter(
      collision => !ROADMAP_COLUMNS.some(col => col.id === collision.id)
    );
    
    // If we found card collisions, use them (for reordering within columns)
    if (cardCollisions.length > 0) {
      return cardCollisions;
    }
    
    // Fall back to pointerWithin for column detection (cross-column moves)
    // This works better for empty columns and collapsed columns
    return pointerWithin(args);
  };

  // ===========================================
  // Group roadmap items by status
  // ===========================================
  const itemsByStatus = useMemo(() => {
    const grouped = {};
    
    // Initialize empty arrays for each column
    ROADMAP_COLUMNS.forEach(col => {
      grouped[col.id] = [];
    });
    
    // Sort items into their respective columns based on status
    roadmap.forEach(item => {
      const status = item.status || 'pending';
      if (grouped[status]) {
        grouped[status].push(item);
      } else {
        // Fallback to pending if status is unknown
        grouped['pending'].push(item);
      }
    });
    
    // Sort each column by priority (high first) then by target date
    Object.keys(grouped).forEach(status => {
      grouped[status].sort((a, b) => {
        // Priority order: high > medium > low
        const priorityOrder = { high: 1, medium: 2, low: 3 };
        const aPriority = priorityOrder[a.priority] || 2;
        const bPriority = priorityOrder[b.priority] || 2;
        
        if (aPriority !== bPriority) {
          return aPriority - bPriority;
        }
        
        // Then by target date (earlier first)
        if (a.targetDate && b.targetDate) {
          return new Date(a.targetDate) - new Date(b.targetDate);
        }
        return 0;
      });
    });
    
    return grouped;
  }, [roadmap]);

  // ===========================================
  // Drag Event Handlers
  // ===========================================
  
  // When drag starts, track the active item for the overlay
  const handleDragStart = (event) => {
    const { active } = event;
    // Find item by either _id or id (MongoDB compatibility)
    const draggedItem = roadmap.find(item => (item._id || item.id) === active.id);
    setActiveItem(draggedItem);
  };

  // When drag ends, update the item's status if moved to a different column
  const handleDragEnd = async (event) => {
    const { active, over } = event;
    
    // Clear active item
    setActiveItem(null);
    
    // If no drop target, do nothing
    if (!over) return;
    
    // Find the dragged item (handle both _id and id for MongoDB compatibility)
    const draggedItem = roadmap.find(item => (item._id || item.id) === active.id);
    if (!draggedItem) return;
    
    // Determine the target column (status)
    let targetStatus = over.id;
    
    // Check if we dropped on another card instead of the column
    const targetItem = roadmap.find(item => (item._id || item.id) === over.id);
    if (targetItem) {
      targetStatus = targetItem.status;
    }
    
    // Verify this is a valid status
    const isValidStatus = ROADMAP_COLUMNS.some(col => col.id === targetStatus);
    if (!isValidStatus) return;
    
    // Only update if the status actually changed
    if (draggedItem.status !== targetStatus) {
      // Use _id or id for the API call
      const itemId = draggedItem._id || draggedItem.id;
      await updateMilestone(projectId, itemId, {
        status: targetStatus
      });
    }
  };

  // Handle drag cancellation
  const handleDragCancel = () => {
    setActiveItem(null);
  };

  // Handle add button click per column - opens modal with default status
  const handleAddClick = (defaultStatus) => {
    onAddMilestone(defaultStatus);
  };

  return (
    <div className="roadmap-kanban">
      <DndContext
        sensors={sensors}
        collisionDetection={collisionDetectionStrategy}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        onDragCancel={handleDragCancel}
      >
        <div className="roadmap-kanban__columns">
          {ROADMAP_COLUMNS.map(column => (
            <RoadmapColumn
              key={column.id}
              column={column}
              items={itemsByStatus[column.id] || []}
              onAddClick={handleAddClick}
              projectId={projectId}
              onEditItem={onEditMilestone}
              isCollapsed={collapsedColumns.has(column.id)}
              onToggleCollapse={handleToggleCollapse}
            />
          ))}
        </div>

        {/* Drag Overlay - Shows floating card while dragging */}
        <DragOverlay>
          {activeItem && (
            <div className="roadmap-card roadmap-card--overlay">
              <RoadmapCard item={activeItem} projectId={projectId} isDragging />
            </div>
          )}
        </DragOverlay>
      </DndContext>
    </div>
  );
};

export default RoadmapKanban;

